import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Briefcase, CheckCircle2, Pencil, Plus, Search, ToggleLeft, ToggleRight } from 'lucide-react';
import APIService from '../services/APIService';
import EmptyState from './EmptyState';
import LoadingSpinner from './LoadingSpinner';

const initialJobForm = {
  title: '',
  company_name: '',
  country: '',
  city: '',
  job_type: 'full_time',
  salary_range_min: '',
  salary_range_max: '',
  currency: 'UGx',
  requirements: '',
  benefits: '',
  application_deadline: ''
};

const jobTypes = ['full_time', 'part_time', 'contract', 'temporary'];
const featuredJobCategories = [
  'House Maids',
  'House Cleaners',
  'Female Drivers',
  'Nurses',
  'Home Teachers',
  'Female Baristas'
];

const statusFilterOptions = [
  { value: 'all', label: 'All jobs' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' }
];

function formatJobType(jobType) {
  return (jobType || '').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeJobPayload(form) {
  return {
    ...form,
    salary_range_min: form.salary_range_min === '' ? null : Number(form.salary_range_min),
    salary_range_max: form.salary_range_max === '' ? null : Number(form.salary_range_max),
    application_deadline: form.application_deadline || null,
    city: form.city || null,
    requirements: form.requirements || null,
    benefits: form.benefits || null
  };
}

function validateJobForm(form) {
  if (!form.title.trim()) {
    return 'Job title is required.';
  }
  if (!form.company_name.trim()) {
    return 'Company name is required.';
  }
  if (!form.country.trim()) {
    return 'Country is required.';
  }
  if (
    form.salary_range_min !== '' &&
    form.salary_range_max !== '' &&
    Number(form.salary_range_min) > Number(form.salary_range_max)
  ) {
    return 'Minimum salary cannot be greater than maximum salary.';
  }
  return null;
}

function JobForm({
  title,
  form,
  onChange,
  onSubmit,
  onCancel,
  submitLabel,
  submitting
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-5">
      <div className="flex items-center justify-between gap-4">
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        {onCancel ? (
          <button type="button" onClick={onCancel} className="text-sm font-medium text-slate-500 hover:text-slate-700">
            Cancel
          </button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Job category or title</label>
          <input
            list="job-category-suggestions"
            name="title"
            value={form.title}
            onChange={onChange}
            required
            placeholder="Example: Nurses"
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-[var(--gc-blue)] focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
          <datalist id="job-category-suggestions">
            {featuredJobCategories.map((category) => (
              <option key={category} value={category} />
            ))}
          </datalist>
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Company name</label>
          <input
            name="company_name"
            value={form.company_name}
            onChange={onChange}
            required
            placeholder="Employer or partner company"
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-[var(--gc-blue)] focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Country</label>
          <input
            name="country"
            value={form.country}
            onChange={onChange}
            required
            placeholder="Qatar or UAE"
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-[var(--gc-blue)] focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">City</label>
          <input
            name="city"
            value={form.city}
            onChange={onChange}
            placeholder="Optional city"
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-[var(--gc-blue)] focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Job type</label>
          <select
            name="job_type"
            value={form.job_type}
            onChange={onChange}
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-[var(--gc-blue)] focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            {jobTypes.map((jobType) => (
              <option key={jobType} value={jobType}>
                {formatJobType(jobType)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Currency</label>
          <input
            name="currency"
            value={form.currency}
            onChange={onChange}
            placeholder="UGx"
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-[var(--gc-blue)] focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Minimum salary</label>
          <input
            name="salary_range_min"
            value={form.salary_range_min}
            onChange={onChange}
            type="number"
            min="0"
            step="0.01"
            placeholder="Optional"
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-[var(--gc-blue)] focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Maximum salary</label>
          <input
            name="salary_range_max"
            value={form.salary_range_max}
            onChange={onChange}
            type="number"
            min="0"
            step="0.01"
            placeholder="Optional"
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-[var(--gc-blue)] focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Application deadline</label>
          <input
            name="application_deadline"
            value={form.application_deadline}
            onChange={onChange}
            type="date"
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-[var(--gc-blue)] focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        <div className="md:col-span-2">
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Requirements</label>
          <textarea
            name="requirements"
            value={form.requirements}
            onChange={onChange}
            rows={3}
            placeholder="Short summary of job requirements"
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-[var(--gc-blue)] focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        <div className="md:col-span-2">
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Benefits</label>
          <textarea
            name="benefits"
            value={form.benefits}
            onChange={onChange}
            rows={3}
            placeholder="Optional benefits and support notes"
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-[var(--gc-blue)] focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="inline-flex items-center gap-2 rounded-full bg-[var(--gc-blue)] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[var(--gc-blue-dark)] disabled:cursor-not-allowed disabled:opacity-70"
      >
        <Plus className="h-4 w-4" />
        {submitting ? 'Saving...' : submitLabel}
      </button>
    </form>
  );
}

const AdminJobsTab = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [filters, setFilters] = useState({ search: '', status_filter: 'all' });
  const [createForm, setCreateForm] = useState(initialJobForm);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(initialJobForm);
  const [actioningJobId, setActioningJobId] = useState(null);

  const countries = useMemo(
    () => [...new Set(jobs.map((job) => job.country).filter(Boolean))].sort(),
    [jobs]
  );

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const jobsData = await APIService.getAdminJobs(filters);
      setJobs(Array.isArray(jobsData) ? jobsData : []);
    } catch (err) {
      setError(err.message || 'Failed to load jobs.');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const handleCreateChange = (event) => {
    const { name, value } = event.target;
    setCreateForm((current) => ({ ...current, [name]: value }));
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditForm((current) => ({ ...current, [name]: value }));
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const handleCreateJob = async (event) => {
    event.preventDefault();
    const validationError = validateJobForm(createForm);
    if (validationError) {
      setError(validationError);
      setSuccess(null);
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await APIService.createJob(normalizeJobPayload(createForm));
      setCreateForm(initialJobForm);
      setSuccess('Job created successfully.');
      await loadJobs();
    } catch (err) {
      setError(err.message || 'Failed to create job.');
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (job) => {
    setEditingId(job.id);
    setEditForm({
      title: job.title || '',
      company_name: job.company_name || '',
      country: job.country || '',
      city: job.city || '',
      job_type: job.job_type || 'full_time',
      salary_range_min: job.salary_range_min ?? '',
      salary_range_max: job.salary_range_max ?? '',
      currency: job.currency || 'UGx',
      requirements: job.requirements || '',
      benefits: job.benefits || '',
      application_deadline: job.application_deadline ? String(job.application_deadline).split('T')[0] : ''
    });
    setError(null);
    setSuccess(null);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm(initialJobForm);
  };

  const handleUpdateJob = async (event) => {
    event.preventDefault();
    const validationError = validateJobForm(editForm);
    if (validationError) {
      setError(validationError);
      setSuccess(null);
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await APIService.updateJob(editingId, normalizeJobPayload(editForm));
      setSuccess('Job updated successfully.');
      cancelEditing();
      await loadJobs();
    } catch (err) {
      setError(err.message || 'Failed to update job.');
    } finally {
      setSaving(false);
    }
  };

  const toggleJobStatus = async (job) => {
    setActioningJobId(job.id);
    setError(null);
    setSuccess(null);
    try {
      if (job.is_active) {
        await APIService.deleteJob(job.id);
        setSuccess('Job deactivated successfully.');
      } else {
        await APIService.activateJob(job.id);
        setSuccess('Job activated successfully.');
      }
      await loadJobs();
    } catch (err) {
      setError(err.message || 'Failed to update job status.');
    } finally {
      setActioningJobId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Job Management</h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              Create, update, activate, and pause Gulf job opportunities for clients. Inactive jobs stay in the system
              for reporting and existing applications.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-2xl bg-slate-50 px-4 py-3 text-center">
              <div className="text-2xl font-bold text-slate-900">{jobs.length}</div>
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Visible jobs</div>
            </div>
            <div className="rounded-2xl bg-emerald-50 px-4 py-3 text-center">
              <div className="text-2xl font-bold text-emerald-700">{jobs.filter((job) => job.is_active).length}</div>
              <div className="text-xs font-medium uppercase tracking-wide text-emerald-600">Active</div>
            </div>
            <div className="rounded-2xl bg-blue-50 px-4 py-3 text-center">
              <div className="text-2xl font-bold text-blue-700">{countries.length}</div>
              <div className="text-xs font-medium uppercase tracking-wide text-blue-600">Countries</div>
            </div>
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}
      {success ? (
        <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{success}</div>
      ) : null}

      <JobForm
        title="Add a new job"
        form={createForm}
        onChange={handleCreateChange}
        onSubmit={handleCreateJob}
        submitLabel="Add Job"
        submitting={saving && !editingId}
      />

      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Available jobs</h3>
            <p className="mt-1 text-sm text-slate-600">Search and filter jobs without leaving the dashboard.</p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:min-w-[28rem] lg:grid-cols-[1fr_180px]">
            <label className="relative block">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search title, company, or country"
                className="w-full rounded-xl border border-slate-300 py-2.5 pl-9 pr-3 text-sm focus:border-[var(--gc-blue)] focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
            </label>

            <select
              name="status_filter"
              value={filters.status_filter}
              onChange={handleFilterChange}
              className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-[var(--gc-blue)] focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              {statusFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-6">
          {loading ? (
            <LoadingSpinner message="Loading jobs..." />
          ) : jobs.length === 0 ? (
            <EmptyState
              icon={Briefcase}
              title="No jobs yet"
              description="When you add job opportunities for Gulf Consultant clients, they will appear here with their status and application counts."
              actionLabel="Add Job"
              onAction={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            />
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => (
                <div key={job.id} className="rounded-2xl border border-slate-200 p-5">
                  {editingId === job.id ? (
                    <JobForm
                      title={`Edit ${job.title}`}
                      form={editForm}
                      onChange={handleEditChange}
                      onSubmit={handleUpdateJob}
                      onCancel={cancelEditing}
                      submitLabel="Save changes"
                      submitting={saving}
                    />
                  ) : (
                    <>
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <h4 className="text-lg font-semibold text-slate-900">{job.title}</h4>
                            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${job.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}`}>
                              {job.is_active ? 'Active' : 'Inactive'}
                            </span>
                            <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                              {formatJobType(job.job_type)}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600">
                            {job.company_name} • {job.city ? `${job.city}, ` : ''}{job.country}
                          </p>
                          <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                            <span>
                              Salary:{' '}
                              {job.salary_range_min || job.salary_range_max
                                ? `${job.currency} ${job.salary_range_min ?? '—'} - ${job.salary_range_max ?? '—'}`
                                : 'Not specified'}
                            </span>
                            <span>
                              Deadline:{' '}
                              {job.application_deadline
                                ? new Date(job.application_deadline).toLocaleDateString()
                                : 'Open until filled'}
                            </span>
                            <span>Applications: {job.application_count || 0}</span>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => startEditing(job)}
                            className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                          >
                            <Pencil className="h-4 w-4" />
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleJobStatus(job)}
                            disabled={actioningJobId === job.id}
                            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-70 ${job.is_active ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                          >
                            {job.is_active ? <ToggleLeft className="h-4 w-4" /> : <ToggleRight className="h-4 w-4" />}
                            {actioningJobId === job.id ? 'Updating...' : job.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </div>

                      {(job.requirements || job.benefits) ? (
                        <div className="mt-4 grid gap-4 md:grid-cols-2">
                          <div className="rounded-2xl bg-slate-50 p-4">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Requirements</p>
                            <p className="mt-2 text-sm leading-6 text-slate-700">{job.requirements || 'No requirements added yet.'}</p>
                          </div>
                          <div className="rounded-2xl bg-slate-50 p-4">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Benefits</p>
                            <p className="mt-2 text-sm leading-6 text-slate-700">{job.benefits || 'No benefits added yet.'}</p>
                          </div>
                        </div>
                      ) : null}

                      <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        {job.is_active
                          ? 'Clients can see and apply for this job.'
                          : 'This job is hidden from clients but kept for existing records.'}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminJobsTab;
